import * as React from 'react';
import { createContext, useState, useEffect } from 'react';
import {
    apiLogin,
    apiLogout,
    apiGetCurrentUser,
    apiSearchCompanies,
    apiSearchCompanyBasicData,
    apiGetKeyFigures,
    apiFetchDecisionMakers,
    apiGetCompanyConcernInformation,
    apiFetchManagementBoard,
    apiFetchBusinessIdHistory,
    apiFetchAnalysisPdf
} from 'api';
import {
    AppContextInterface,
    AppState,
    AuthInfo,
    StatementKeyFigures,
    KeyFiguresResponse,
    User
} from './types';
import config from '../../config';
import Company from '../../types/company';
import { notification } from 'antd';

interface Props {
    children: React.ReactNode;
}

export const AppContext = createContext<AppContextInterface | null>(null);

export default function AppContextProvider(props: Props) {
    const [auth, setAuth] = useState<AuthInfo>({
        authenticated: false,
        firstName: '',
        endUserId: ''
    });

    const [companyPageBasicInfo, setCompanyPageBasicInfo] = useState<
        Company | undefined
    >(undefined);

    const [companyPageKeyFigures, setCompanyPageKeyFigures] = useState<
        StatementKeyFigures[] | undefined
    >(undefined);

    const [
        companyPageConcernKeyFigures,
        setCompanyPageConcernKeyFigures
    ] = useState<StatementKeyFigures[] | undefined>(undefined);

    const [isConcernSelected, setIsConcernSelected] = useState<boolean>(false);

    const initializeCompanyPage = (businessId: string) => {
        searchCompanyBasicData(businessId)
            .then(res => res.json())
            .then((data: Company) => {
                setCompanyPageBasicInfo(data);
            })
            .catch(e => {
                notification.error({
                    message: `Tapahtui virhe ${e.status}`,
                    description:
                        'Yrityksen perustietojen hakemisessa tapahtui virhe. Ota yhteyttä asiakaspalveluun.',
                    duration: null
                });
            });

        fetchKeyFigures(businessId, 10)
            .then(res => res.json())
            .then((data: KeyFiguresResponse) => {
                setCompanyPageKeyFigures(data.keyFigures);
                setCompanyPageConcernKeyFigures(data.concernKeyFigures);
                if (data.concernKeyFigures.length > 0) {
                    setIsConcernSelected(true);
                }
            })
            .catch(e => {
                notification.error({
                    message: `Tapahtui virhe ${e.status}`,
                    description:
                        'Yrityksen tunnuslukujen hakemisessa tapahtui virhe. Ota yhteyttä asiakaspalveluun.',
                    duration: null
                });
            });
    };

    const cleanUpCompanyPage = () => {
        setCompanyPageBasicInfo(undefined);
        setCompanyPageKeyFigures(undefined);
        setCompanyPageConcernKeyFigures(undefined);
        setIsConcernSelected(false);
    };

    const logout = () => {
        apiLogout().finally(() => {
            setAuth({ authenticated: false, firstName: '' });
        });
    };

    const login = (username: string, password: string) => {
        return apiLogin(username, password).then(response => {
            if (response.ok) {
                response.json().then(user => {
                    setAuth({ authenticated: true, firstName: user.firstName });
                });
            }
            return response;
        });
    };

    const currentUser = (): Promise<Response> => {
        return apiGetCurrentUser();
    };

    const setAuthInfo = (authInfo: AuthInfo) => {
        setAuth(authInfo);
    };

    const searchCompanies = (
        businessIdOrCompanyName: string
    ): Promise<Response> => {
        return apiSearchCompanies(businessIdOrCompanyName);
    };

    const fetchKeyFigures = (
        businessId: string,
        amount?: number
    ): Promise<Response> => {
        return apiGetKeyFigures(businessId, amount);
    };

    const fetchDecisionMakers = (businessId: string): Promise<Response> => {
        return apiFetchDecisionMakers(businessId);
    };

    const fetchBusinessIdHistory = (businessId: string): Promise<Response> => {
        return apiFetchBusinessIdHistory(businessId);
    };

    const fetchManagementBoard = (businessId: string): Promise<Response> => {
        return apiFetchManagementBoard(businessId);
    };

    const fetchAnalysisPdf = (
        businessId: string,
        language: string,
        concern: string
    ): Promise<Response> => {
        return apiFetchAnalysisPdf(businessId, language, concern);
    };

    const searchCompanyBasicData = (businessId: string): Promise<Response> => {
        return apiSearchCompanyBasicData(businessId);
    };

    const getCompanyConcernInformation = (
        businessId: string
    ): Promise<Response> => {
        return apiGetCompanyConcernInformation(businessId);
    };

    const appState: AppState = {
        auth,
        companyPageBasicInfo,
        companyPageKeyFigures,
        companyPageConcernKeyFigures,
        isConcernSelected
    };

    useEffect(() => {
        if (window.location.pathname !== config.loginPagePath) {
            currentUser()
                .then(response => {
                    return response.json();
                })
                .then((user: User) => {
                    setAuth({
                        authenticated: true,
                        firstName: user.firstName || '',
                        endUserId: user.endUserId || ''
                    });
                })
                .catch(() => {
                    setAuth({ authenticated: false, firstName: '' });
                });
        }
    }, []);

    return (
        <AppContext.Provider
            value={{
                actions: {
                    logout,
                    login,
                    currentUser,
                    setAuthInfo,
                    searchCompanies,
                    fetchKeyFigures,
                    searchCompanyBasicData,
                    fetchDecisionMakers,
                    getCompanyConcernInformation,
                    initializeCompanyPage,
                    cleanUpCompanyPage,
                    fetchManagementBoard,
                    fetchBusinessIdHistory,
                    fetchAnalysisPdf,
                    setIsConcernSelected
                },
                state: appState
            }}
        >
            {props.children}
        </AppContext.Provider>
    );
}
